import React, { useState } from "react";
import { Link } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import ShareSheet from "@/components/ShareSheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CATEGORY_LABELS } from "@/lib/brand";
import { timeAgo, deadlineLabel } from "@/lib/format";
import { MessageCircle, Share2, Lock, TrendingUp } from "lucide-react";

function AuthorLine({ author }) {
  const name = author?.display_name || "Anonymous";
  const inner = (
    <div className="flex items-center gap-2 min-w-0">
      <Avatar className="w-8 h-8 border-2 border-ink shrink-0">
        <AvatarImage src={author?.avatar_url} />
        <AvatarFallback className="bg-locked text-xs font-bold">{name[0]?.toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="leading-tight min-w-0">
        <div className="font-semibold text-sm truncate">
          {name}
          {author?.guest && <span className="text-muted-foreground font-normal"> · Guest</span>}
        </div>
        {author?.username && <div className="text-xs text-muted-foreground truncate">@{author.username}</div>}
      </div>
    </div>
  );
  if (author?.username) return <Link to={`/@${author.username}`} onClick={(e) => e.stopPropagation()} className="hover:opacity-80">{inner}</Link>;
  return inner;
}

export default function CallCard({ call }) {
  const [shareOpen, setShareOpen] = useState(false);
  const c = call;
  const totalReactions = Object.values(c.reactions || {}).reduce((a, b) => a + b, 0);
  const called = c.status === "called_it";
  const daysEarly = c.resolution?.days_early_human;

  return (
    <div data-testid={`call-card-${c.short_id}`}
      className={`card-brutal p-5 relative animate-slide-up ${called ? "bg-called/15" : "bg-white"}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <AuthorLine author={c.author} />
        <StatusBadge status={c.status} />
      </div>

      <Link to={`/c/${c.short_id}`} className="block group">
        {c.sealed && !c.sealed_revealed ? (
          <div className="font-display text-lg font-semibold flex items-center gap-2 text-muted-foreground">
            <Lock className="w-4 h-4" /> 🔐 Sealed Call — revealed {String(c.sealed_reveal_at || "").slice(0, 10)}
          </div>
        ) : (
          <p className="font-display text-xl sm:text-2xl font-semibold leading-snug tracking-tight group-hover:text-brand transition-colors">
            “{c.prediction_text}”
          </p>
        )}
      </Link>

      {called && daysEarly && (
        <div className="mt-2 inline-flex items-center gap-1 text-called font-bold text-sm">
          <TrendingUp className="w-4 h-4" strokeWidth={3} /> Called {daysEarly}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-4 text-xs font-mono">
        {c.category && <span className="border-2 border-ink px-2 py-0.5 bg-secondary">{CATEGORY_LABELS[c.category] || c.category}</span>}
        {c.confidence && <span className="border-2 border-ink px-2 py-0.5 bg-brand text-white">{c.confidence}%</span>}
        <span className="text-muted-foreground">{deadlineLabel(c.deadline_type, c.deadline_value)}</span>
        <span className="text-muted-foreground">· {timeAgo(c.created_at)}</span>
      </div>

      <div className="dashed-tear mt-4 pt-3 flex items-center justify-between text-sm">
        <div className="flex items-center gap-4 text-muted-foreground">
          {totalReactions > 0 && <span className="font-semibold text-ink">{totalReactions} reactions</span>}
          <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {c.comment_count || 0}</span>
          {c.counter_count > 0 && <span className="font-semibold text-brand">{c.counter_count} counter</span>}
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/c/${c.short_id}?counter=1`} data-testid={`counter-cta-${c.short_id}`}
            className="text-xs font-bold text-brand hover:underline">Make your Call</Link>
          <button data-testid={`share-btn-${c.short_id}`} onClick={() => setShareOpen(true)}
            className="w-8 h-8 border-2 border-ink bg-white flex items-center justify-center btn-press shadow-brutal-sm">
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <ShareSheet open={shareOpen} onOpenChange={setShareOpen} shortId={c.short_id}
        predictionText={c.prediction_text} authorName={c.author?.display_name} />
    </div>
  );
}
